import React, { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { PeriodCycle } from '@/core/api/dtos/request/account/updateSubscription'
import { PRODUCT_PLAYER_ID } from '@/core/api/entities/misc/product'
import { Zone } from '@/core/api/entities/zone/zone'
import { useUpdateSubscription } from '@/core/hooks/account/useSubscription'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useZones } from '@/core/hooks/useZone'
import { useTablePagination } from '@/core/hooks/utils/useTable'
import { zoneDeleted } from '@/core/store/slices/zones'
import DeleteZoneModal from '@/features/zones/modals/DeleteZoneModal'
import type { DeleteZoneModalFormData } from '@/features/zones/modals/DeleteZoneModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { SubscriptionCard } from './SubscriptionCard'
import SubscriptionZonesTable from './SubscriptionZonesTable'

interface SubscriptionProps {}

const Subscription: React.FC<SubscriptionProps> = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const account = useCurrentAccount()
	const pagination = useTablePagination({ defaultPage: 1, defaultPageSize: 10 })

	const currentAccount = useCurrentAccount()

	const trialSubscriptions = currentAccount?.getTrialSubscriptions() || []
	const isInTrial = trialSubscriptions.length > 0

	// State for delete modal
	const [selectedZoneToDelete, setSelectedZoneToDelete] = useState<Zone | null>(
		null
	)
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)

	const {
		data: zones,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useZones({
		config: useAccountConfig({
			params: {
				page: pagination.current,
				per_page: pagination.pageSize,
				include: 'state,groups,store.lastGooglePlaceData',
			},
		}),
		options: {
			retry: false,
		},
	})

	// Fetch all zones for the remove zones modal (without pagination)
	const { data: allZones, isLoading: allZonesLoading } = useZones({
		config: useAccountConfig({
			params: {
				per_page: 1000, // Fetch all zones
				include: 'groups',
			},
		}),
		options: {
			retry: false,
		},
	})

	const subscription =
		useMemo(
			() => account?.getActiveSubscriptions?.()?.[0] || null,
			[account?.subscriptions]
		) || null

	// Hook for updating subscription
	const { mutateAsync: updateSubscription } = useUpdateSubscription({
		subscriptionId: subscription?.id ?? 0,
	})

	// The active zones count is the total number of zones currently in use
	const activeZonesCount = subscription?.getPlayerQuantity() ?? 0

	// Handler for updating zones count
	const handleUpdateZones = useCallback(
		async (newZonesCount: number, zonesToRemove?: number[]) => {
			if (!subscription) {
				throw new Error('No active subscription')
			}

			const playerItems = subscription.getPlayerItems()
			if (playerItems.length === 0) {
				throw new Error('No player items found in subscription')
			}

			// Determine period cycle from current subscription
			const periodCycle =
				subscription.periodCycle === 'yearly'
					? PeriodCycle.YEARLY
					: PeriodCycle.MONTHLY

			await updateSubscription({
				plan_id: subscription.planId,
				period_cycle: periodCycle,
				items: playerItems.map((item) => ({
					id: item.id,
					quantity: newZonesCount,
				})),
				players: zonesToRemove,
			})

			// Refetch zones after update
			refetch()
		},
		[subscription, updateSubscription, refetch]
	)

	// Handler for viewing zone details
	const handleViewZoneDetails = useCallback(
		(zone: Zone) => {
			navigate(`/zones/${zone.id}?from=subscription`)
		},
		[navigate]
	)

	// Handler to open delete modal
	const handleOpenDeleteModal = useCallback((zone: Zone) => {
		setSelectedZoneToDelete(zone)
		setIsDeleteModalVisible(true)
	}, [])

	// Handler to close delete modal
	const handleCloseDeleteModal = useCallback(() => {
		setIsDeleteModalVisible(false)
		setSelectedZoneToDelete(null)
	}, [])

	// Handler for confirming zone deletion
	// 1. DELETE the zone entity
	// 2. PATCH the subscription to reduce zone count
	const handleConfirmDeleteZone = useCallback(
		async (data: DeleteZoneModalFormData) => {
			if (!selectedZoneToDelete || !subscription) return

			setIsDeleting(true)

			try {
				const playerItems = subscription.getPlayerItems()
				if (playerItems.length > 0) {
					const currentQuantity = subscription.getPlayerQuantity() ?? 0
					const newQuantity = Math.max(0, currentQuantity - 1)

					const periodCycle =
						subscription.periodCycle === 'yearly'
							? PeriodCycle.YEARLY
							: PeriodCycle.MONTHLY

					await updateSubscription({
						plan_id: subscription.planId,
						period_cycle: periodCycle,
						items: playerItems.map((item) => ({
							id: item.id,
							quantity: newQuantity,
						})),
						players: [selectedZoneToDelete.id],
						password: data.password,
					})
				}

				// Dispatch action to Redux
				dispatch(zoneDeleted(selectedZoneToDelete.id))

				// Show success notification
				showNotification({
					type: 'success',
					message: t('entities.zone.delete.success.title'),
					description: t('entities.zone.delete.success.description', {
						name: selectedZoneToDelete.name,
					}),
				})

				// Close modal and refetch
				setIsDeleteModalVisible(false)
				setSelectedZoneToDelete(null)
				refetch()
			} catch (error) {
				console.error('Failed to delete zone:', error)
				showNotification({
					type: 'error',
					message: t('entities.zone.delete.error.title'),
					description: t('entities.zone.delete.error.description', {
						name: selectedZoneToDelete?.name,
					}),
				})
			} finally {
				setIsDeleting(false)
			}
		},
		[
			selectedZoneToDelete,
			subscription,
			updateSubscription,
			dispatch,
			showNotification,
			t,
			refetch,
		]
	)

	const billingSummaryData = useMemo(
		() => ({
			planName: subscription?.plan?.name,
			activeZonesCount,
			totalPrice: subscription?.total || 0,
			pricePerZone: subscription?.getProductPrice(PRODUCT_PLAYER_ID) ?? null,
			currency: subscription?.currency || 'USD',
			periodCycle: subscription?.periodCycle || 'monthly',
			nextBillingAt: subscription?.nextBillingAt?.toISO(),
			trialEndAt: subscription?.trialEndAt?.toISO(),
			status: subscription?.status,
		}),
		[subscription, activeZonesCount]
	)

	return (
		<>
			{/* Delete Zone Modal */}
			<DeleteZoneModal
				visible={isDeleteModalVisible}
				zone={selectedZoneToDelete}
				onClose={handleCloseDeleteModal}
				onConfirm={handleConfirmDeleteZone}
				isDeleting={isDeleting}
				onPage="subscriptions"
			/>

			<div className="space-y-8  mb-2">
				<div className="text-xl font-semibold leading-none tracking-tight text-medium mb-[19px]">
					{t('account.subscription.your_plan')}
				</div>
				{/* Subscription Summary Card */}
				<SubscriptionCard
					planName={billingSummaryData.planName || ''}
					activeZonesCount={billingSummaryData.activeZonesCount || 0}
					totalPrice={billingSummaryData.totalPrice || 0}
					pricePerZone={billingSummaryData.pricePerZone}
					currency={billingSummaryData.currency || ''}
					periodCycle={billingSummaryData.periodCycle || ''}
					nextBillingAt={billingSummaryData.nextBillingAt || ''}
					trialEndAt={billingSummaryData.trialEndAt || ''}
					status={billingSummaryData.status || ''}
					isInTrial={isInTrial}
					zones={allZones?.data || []}
					zonesLoading={allZonesLoading}
					onUpdateZones={handleUpdateZones}
					subscription={subscription || undefined}
				/>
			</div>

			{/* Zones table */}
			<div className="space-y-8 hidden lg:block">
				<div className="text-xl font-semibold leading-none tracking-tight text-medium mb-[19px]">
					Zones
				</div>
				<SubscriptionZonesTable
					zones={zones}
					isLoading={isLoading}
					isFetching={isFetching}
					error={error}
					refetch={refetch}
					currentPage={pagination.current}
					totalPages={zones?.meta?.pagination?.total_pages || 1}
					pageSize={pagination.pageSize}
					onPageChange={pagination.onChange}
					onPageSizeChange={(size) => pagination.onShowSizeChange(1, size)}
					onViewZoneDetails={handleViewZoneDetails}
					onDeleteZone={handleOpenDeleteModal}
				/>
			</div>
		</>
	)
}

export default Subscription
